/**
 * ThermalPrinterContext.js
 * 
 * Context global para impresión térmica BLE.
 * 
 * - Maneja conexión/desconexión con la impresora
 * - Detecta perfil automáticamente (escpos-58, cat-58, etc.)
 * - Persiste configuración en localStorage (no el permiso BLE, eso Chrome lo pide siempre)
 * - Expone print(doc) para imprimir desde cualquier componente
 * 
 * Uso:
 *   <ThermalPrinterProvider>
 *     <App />
 *   </ThermalPrinterProvider>
 * 
 *   const { connected, connect, print } = useThermalPrinter();
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
const thermalPrint = require('./thermal-print.cjs.js');
const { Printer, BleTransport, createDocument, renderText: _renderTextFn } = thermalPrint;

// ─── Context ────────────────────────────────────────────────────────
const ThermalPrinterCtx = createContext({
    connected: false,
    connecting: false,
    profileName: null,
    error: null,
    connect: async () => {},
    disconnect: async () => {},
    print: async () => {},
    /** Genera preview texto monoespaciado (32 o 48 cols según perfil) */
    renderText: () => "",
});

export const useThermalPrinter = () => useContext(ThermalPrinterCtx);

// ─── Provider ───────────────────────────────────────────────────────
export function ThermalPrinterProvider({ children }) {
    const [connected, setConnected] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [profileName, setProfileName] = useState(
        () => localStorage.getItem("thermal_profile") || null
    );
    const [error, setError] = useState(null);

    const transportRef = useRef(null);
    const printerRef = useRef(null);


    // ── connect ─────────────────────────────────────────────────────
    const connect = useCallback(async () => {
        setError(null);
        setConnecting(true);

        try {
            const transport = new BleTransport();
            const savedProfile = localStorage.getItem("thermal_profile");
            const printer = new Printer({
                profile: savedProfile || "escpos-58",
                transport,
            });
            await printer.requestDevice();
            await printer.connect({ autoProfile: true });

            const detected = (printer.profile && printer.profile.name) || savedProfile || "escpos-58";
            localStorage.setItem("thermal_profile", detected);
            localStorage.setItem("thermal_configured", "true");

            printerRef.current = printer;
            setProfileName(detected);
            setConnected(true);

            // Escuchar desconexión
            printer.on("disconnect", () => {
                setConnected(false);
                printerRef.current = null;
            });
        } catch (err) {
            // Si el usuario canceló el selector, no es error real
            if (err && err.name === "NotFoundError") {
                setError(null);
            } else {
                console.error("[ThermalPrint] connect error:", err);
                setError((err && err.message) || "Error al conectar la impresora");
            }
        } finally {
            setConnecting(false);
        }
    }, []);

    // ── disconnect ──────────────────────────────────────────────────
    const disconnect = useCallback(async () => {
        try {
            if (printerRef.current) {
                await printerRef.current.disconnect();
            }
        } catch (_) {}
        printerRef.current = null;
        setConnected(false);
    }, []);

    // ── print ───────────────────────────────────────────────────────
    const print = useCallback(async (doc) => {
        if (!printerRef.current) {
            throw new Error("Impresora no conectada");
        }
        setError(null);
        try {
            await printerRef.current.print(doc);
        } catch (err) {
            console.error("[ThermalPrint] print error:", err);
            setError((err && err.message) || "Error al imprimir");
            throw err;
        }
    }, []);

    // ── renderText (preview) ────────────────────────────────────────
    const renderText = useCallback((doc) => {
        // render/text.js funciona sin browser — genera texto monoespaciado
        // Si la librería expone renderText directamente, usarlo;
        // sino se puede importar desde render/text.js
        try {
            const { renderText: _renderText } = require("../../../lib/thermal-print/render/text.js");
            return _renderText(doc, { columns: (profileName && profileName.includes("80")) ? 48 : 32 });
        } catch (_) {
            return null;
        }
    }, [profileName]);

    // ── value ───────────────────────────────────────────────────────
    const value = {
        connected,
        connecting,
        profileName,
        error,
        connect,
        disconnect,
        print,
        renderText,
    };

    return (
        <ThermalPrinterCtx.Provider value={value}>
            {children}
        </ThermalPrinterCtx.Provider>
    );
}

export default ThermalPrinterCtx;